import {
    addNode,
    appendWire,
    cloneSubgraph,
    createGraph,
    findNode,
    findWire,
    GRAPH_VERSION,
    isEmptyGraph,
    mergeSubgraph,
    moveNode,
    removeNode,
    removeObjectRefs,
    removeWire,
    reorderNodeOutputs,
    replaceNode,
    setNodeData,
    updateNodeData,
} from '../graph/graphModel.js';
import { statePropertyOptions, stateTargetOptions } from '../gpio/gpioEditorModel.js';
import {
    getEventChoices,
    getLogicNodePort,
    getObjectBindingsPort,
    getObjectEventsPort,
    isPortCompatible,
    CONDITION_OPERATORS,
    PORT_KIND,
    SWITCH_ELSE_PORT,
    switchCasePort,
} from '../Hasp/properties/v2/logic/index.js';
import { gpioNodeEventChoices, isGpioNode } from './gpioLogic.js';

/**
 * Event/action node graph of one page — the Logic domain on top of the
 * generic graph model (graph/graphModel.js), which supplies the pure,
 * immutable editing operations re-exported here.
 *
 * Shape: { version, nodes: LogicNode[], wires: LogicWire[] }
 *   LogicNode: { id, kind: 'event'|'action'|'gpio'|'binding'|'condition'|'switch', type, x, y, data }
 *   LogicWire: { id, from: { object: <uuid>, port } | { node, port }, to: { node, port } | { object, port }, order? }
 *
 *   (object|GPIO) ─▶ Event ─▶ [Condition ─then▶ | Switch ─case▶]* ─▶ Action
 *   (object|GPIO) ─▶ Condition/Switch `subject`: the value they compare
 *   GPIO ─state▶ GPIO binding ─value▶ (object): a gpio_bindings.state entry
 *
 * UI objects are referenced by their Designer uuid and looked up through a
 * context `{ getObject(uuid), getGpioEntity?(id) }` that only resolves objects
 * of this page. An event node's source is a UI object (`from.object`) or a
 * GPIO entity node (`from.node`). Node coordinates are page-local.
 */
export const LOGIC_GRAPH_VERSION = GRAPH_VERSION;

/** Node id prefix per kind. */
export const LOGIC_ID_PREFIX = Object.freeze({ event: 'ev', action: 'ac', gpio: 'gp', binding: 'gb', condition: 'cd', switch: 'sw' });

export {
    addNode, findNode, findWire, moveNode, removeNode, removeObjectRefs, removeWire, replaceNode, setNodeData, updateNodeData,
};

export function createLogicGraph() {
    return createGraph();
}

export function isEmptyLogicGraph(graph) {
    return isEmptyGraph(graph);
}

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** Condition and switch nodes: between an event and its actions. */
export const isFlowNode = node => node?.kind === 'condition' || node?.kind === 'switch';

export function getEventSourceWire(graph, eventNodeId) {
    return graph.wires.find(wire => wire.to.node === eventNodeId && wire.to.port === 'source') ?? null;
}

/** The UI object (uuid) an event node listens to; null for a GPIO source. */
export function getEventSource(graph, eventNodeId) {
    return getEventSourceWire(graph, eventNodeId)?.from.object ?? null;
}

/** An event node's source endpoint: `{ object, port }`, `{ node, port }` (GPIO) or null. */
export function getEventSourceRef(graph, eventNodeId) {
    return getEventSourceWire(graph, eventNodeId)?.from ?? null;
}

const sameSource = (a, b) => Boolean(a && b) &&
    (a.object !== undefined ? a.object === b.object : a.node !== undefined && a.node === b.node);

/** Events offered by a source endpoint (UI object or GPIO node). */
function sourceEventChoices(graph, ref, context) {
    if (ref?.object !== undefined) {
        const object = context.getObject?.(ref.object);
        return object ? getEventChoices(object) : null;
    }
    const node = findNode(graph, ref?.node);
    return isGpioNode(node) ? gpioNodeEventChoices(node, context) : null;
}

/** GPIO event nodes of a GPIO node. */
export function getEventNodesForSource(graph, ref) {
    return graph.nodes.filter(node => node.kind === 'event' && sameSource(getEventSourceRef(graph, node.id), ref));
}

/** Wires from an event node to its actions, in execution order. */
export function getEventActionWires(graph, eventNodeId) {
    return graph.wires.filter(wire => wire.from.node === eventNodeId && wire.from.port === 'actions').sort(byOrder);
}

export function getEventNodesForObject(graph, objectUuid) {
    return graph.nodes.filter(node => node.kind === 'event' && getEventSource(graph, node.id) === objectUuid);
}

/** Nodes triggering an action, condition or switch node: events, conditions or switches. */
export function getActionTriggers(graph, actionNodeId) {
    return graph.wires
        .filter(wire => wire.to.node === actionNodeId && wire.to.port === 'trigger')
        .map(wire => wire.from.node);
}

/** Wires leaving one output of a node, in execution order. */
export function getOutputWires(graph, nodeId, port) {
    return graph.wires.filter(wire => wire.from.node === nodeId && wire.from.port === port).sort(byOrder);
}

/** Output ports of a condition (`then`) or switch (its cases, then `else`). */
export function getFlowOutputPorts(node) {
    if (node?.kind === 'condition') return ['then'];
    if (node?.kind === 'switch') return [...(node.data?.cases ?? []).map(item => switchCasePort(item.id)), SWITCH_ELSE_PORT];
    return [];
}

/** The event nodes a node runs for: wired to it directly, or through conditions and switches. */
export function getRootEvents(graph, nodeId, seen = new Set([nodeId])) {
    return getActionTriggers(graph, nodeId).flatMap(id => {
        if (seen.has(id)) return [];
        seen.add(id);
        const node = findNode(graph, id);
        if (node?.kind === 'event') return [node];
        return isFlowNode(node) ? getRootEvents(graph, id, seen) : [];
    });
}

/** Whether a node runs behind a condition or switch. */
export function isConditional(graph, nodeId) {
    return getActionTriggers(graph, nodeId).some(id => isFlowNode(findNode(graph, id)));
}

/** The wire into a condition's or switch's `subject`, or null (it then compares the event value). */
export function getSubjectWire(graph, nodeId) {
    return graph.wires.find(wire => wire.to.node === nodeId && wire.to.port === 'subject') ?? null;
}

/** Whether `goalId` runs after `startId`, following trigger wires. */
function reaches(graph, startId, goalId, seen = new Set()) {
    if (startId === goalId) return true;
    if (seen.has(startId)) return false;
    seen.add(startId);
    return graph.wires.some(wire => wire.from.node === startId && wire.to.port === 'trigger' &&
        reaches(graph, wire.to.node, goalId, seen));
}

function resolveInput(graph, to, context) {
    if (to?.object !== undefined) {
        const object = context.getObject?.(to.object);
        if (!object) return { error: 'Object is not on this page' };
        const port = getObjectBindingsPort(object);
        if (!port || port.id !== to.port) return { error: 'This object takes no GPIO binding' };
        return { port, object };
    }
    const node = findNode(graph, to?.node);
    if (!node) return { error: 'Target node does not exist' };
    const port = getLogicNodePort(node, 'in', to.port);
    return port ? { port, node } : { error: 'Unknown input port' };
}

/** Why a GPIO binding cannot drive `object`, or ''. */
export function bindingTargetError(data, object) {
    const supported = data?.property && statePropertyOptions(object.obj).includes(data.property);
    const targets = data?.property ? (supported ? stateTargetOptions(object.obj, object, data.property) : []) : null;
    if (targets && targets.length === 0) return `"${data.property}" cannot drive this ${object.obj}`;
    if (targets && data.target_property && !targets.includes(data.target_property)) {
        return `"${data.property}" cannot drive ${data.target_property} of this ${object.obj}`;
    }
    return '';
}

/** The GPIO entity node a binding reads, or null. */
export function getBindingSource(graph, bindingId) {
    const wire = graph.wires.find(item => item.to.node === bindingId && item.to.port === 'source');
    return wire ? findNode(graph, wire.from.node) : null;
}

/** UI objects (uuids) a binding drives, in wire order. */
export function getBindingTargets(graph, bindingId) {
    return graph.wires
        .filter(wire => wire.from.node === bindingId && wire.from.port === 'value' && wire.to.object !== undefined)
        .map(wire => wire.to.object);
}

function resolveOutput(graph, from, context) {
    if (from?.object !== undefined) {
        const object = context.getObject?.(from.object);
        if (!object) return { error: 'Object is not on this page' };
        const port = getObjectEventsPort(object);
        if (!port || port.id !== from.port) return { error: 'Object emits no events' };
        return { port, object };
    }
    const node = findNode(graph, from?.node);
    if (!node) return { error: 'Source node does not exist' };
    const port = getLogicNodePort(node, 'out', from.port);
    return port ? { port, node } : { error: 'Unknown output port' };
}

/** Another event node already handles `event` for the source `ref`. */
function eventTaken(graph, ref, event, exceptNodeId) {
    return Boolean(event) && getEventNodesForSource(graph, ref)
        .some(node => node.id !== exceptNodeId && node.data?.event === event);
}

/**
 * Check a prospective wire. Static rules come from the port declarations in
 * the logic schema; this adds the rules that need graph state.
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export function canConnect(graph, from, to, context = {}) {
    const fail = reason => ({ ok: false, reason });
    const end = resolveInput(graph, to, context);
    if (end.error) return fail(end.error);
    const { port: input, node: target } = end;
    const output = resolveOutput(graph, from, context);
    if (output.error) return fail(output.error);
    if (from.node !== undefined && from.node === to.node) return fail('Cannot connect a node to itself');
    if (!isPortCompatible(output.port, input)) {
        if (output.port.provides !== PORT_KIND.OBJECT || !input.accepts.includes(PORT_KIND.TRIGGER)) {
            return fail(input.accepts.includes(PORT_KIND.GPIO_STATE) ? 'A GPIO binding reads the State of a GPIO entity'
                : input.accepts.includes(PORT_KIND.VALUE) ? 'Wire a GPIO binding to this object'
                    : 'These ports cannot be connected');
        }
        return fail(isFlowNode(target)
            ? 'Conditions and switches run from an event; wire an object to Compare'
            : 'Actions connect to an event, not directly to an object');
    }
    if (to.port === 'subject' && output.object?.obj === 'page') return fail('The page has no value to compare');
    if (to.port === 'trigger' && from.node !== undefined && reaches(graph, to.node, from.node)) {
        return fail('This would make a loop');
    }
    if (end.object && output.node?.kind === 'binding') {
        const reason = bindingTargetError(output.node.data, end.object);
        if (reason) return fail(reason);
    }
    const sameEnd = wire => wire.to.port === to.port &&
        (to.object !== undefined ? wire.to.object === to.object : wire.to.node === to.node);
    const sameStart = wire => from.object !== undefined
        ? wire.from.object === from.object
        : wire.from.node === from.node && wire.from.port === from.port;
    if (graph.wires.some(wire => sameEnd(wire) && sameStart(wire))) return fail('Already connected');
    if (input.max !== undefined && graph.wires.filter(sameEnd).length >= input.max) {
        return fail(target?.kind === 'event' ? 'Event already has a source' : 'Input is already connected');
    }
    if (target?.kind === 'event' && output.port.provides === PORT_KIND.OBJECT) {
        const event = target.data?.event;
        const what = output.object ? 'object' : 'GPIO entity';
        const choices = sourceEventChoices(graph, from, context) ?? [];
        if (event && !choices.includes(event)) {
            return fail(`Event "${event}" is not available for this ${what}`);
        }
        // One event node per event type: a source takes at most as many event
        // nodes as it has events, whether or not their event is chosen yet.
        if (getEventNodesForSource(graph, from).length >= choices.length) {
            return fail(`This ${what} has no unused events left`);
        }
        if (eventTaken(graph, from, event, target.id)) {
            return fail(`This ${what} already has a "${event}" event node`);
        }
    }
    return { ok: true };
}

/** @returns {{ graph, wire } | { graph, error }} — `graph` is unchanged on error. */
export function connect(graph, from, to, context = {}, { id } = {}) {
    const check = canConnect(graph, from, to, context);
    if (!check.ok) return { graph, error: check.reason };
    let order;
    // What an event, condition or switch output runs is ordered.
    if (from.node !== undefined && to.port === 'trigger') {
        const orders = getOutputWires(graph, from.node, from.port).map(item => item.order ?? 0);
        order = orders.length ? Math.max(...orders) + 1 : 0;
    }
    const end = to.object !== undefined ? { object: to.object, port: to.port } : { node: to.node, port: to.port };
    return appendWire(graph, from, end, { id, order });
}

/** Rewrite action order of an event node; `wireIds` lists its action wires in the new order. */
export function reorderEventActions(graph, eventNodeId, wireIds) {
    return reorderNodeOutputs(graph, eventNodeId, wireIds);
}

/** Event choices for an event node: its source's events, with those already used by a sibling disabled. */
export function getEventNodeChoices(graph, eventNodeId, context = {}) {
    const ref = getEventSourceRef(graph, eventNodeId);
    return (sourceEventChoices(graph, ref, context) ?? []).map(event => ({
        event,
        disabled: eventTaken(graph, ref, event, eventNodeId),
    }));
}

/**
 * Structural diagnostics `{ severity, code, message, nodeId? | wireId? }`.
 * `error` marks inconsistent graph data; `warning` marks incomplete authoring.
 * Neither kind is ever exported.
 */
export function validateLogicGraph(graph, context = {}) {
    const diagnostics = [];
    const report = (severity, code, message, detail) => diagnostics.push({ severity, code, message, ...detail });
    const nodeIds = new Set(graph.nodes.map(node => node.id));
    const missingNode = end => end.object === undefined && !nodeIds.has(end.node);
    const missingObject = end => end.object !== undefined && !context.getObject?.(end.object);
    for (const wire of graph.wires) {
        if (missingNode(wire.from) || missingNode(wire.to)) {
            report('error', 'dangling-wire', 'Wire references a missing node', { wireId: wire.id });
        } else if (missingObject(wire.from) || missingObject(wire.to)) {
            report('error', 'missing-object', 'Wire references an object that is not on this page', { wireId: wire.id });
        }
    }
    const seen = new Map();
    for (const node of graph.nodes) {
        if (node.kind === 'event') {
            const ref = getEventSourceRef(graph, node.id);
            const choices = sourceEventChoices(graph, ref, context);
            const gpioSource = ref?.node !== undefined ? findNode(graph, ref.node) : null;
            const event = node.data?.event;
            const what = gpioSource ? 'GPIO entity' : 'object';
            if (!choices) report('warning', 'event-no-source', 'Event has no source', { nodeId: node.id });
            else if (gpioSource && !gpioSource.data?.entity) {
                report('warning', 'event-no-source', 'Event source has no GPIO entity selected', { nodeId: node.id });
            } else if (!event) report('warning', 'event-no-name', 'Event has no event selected', { nodeId: node.id });
            else if (!choices.includes(event)) {
                report('error', 'event-unsupported', `Event "${event}" is not available for this ${what}`, { nodeId: node.id });
            } else {
                const key = `${ref.object ?? `gpio:${gpioSource.data.entity}`}\u0000${event}`;
                if (seen.has(key)) report('error', 'event-duplicate', `Duplicate "${event}" event node for this ${what}`, { nodeId: node.id });
                else seen.set(key, node.id);
            }
            if (getEventActionWires(graph, node.id).length === 0) {
                report('warning', 'event-no-actions', 'Event has no actions', { nodeId: node.id });
            }
        } else if (node.kind === 'gpio') {
            if (!node.data?.entity) report('warning', 'gpio-no-entity', 'Select a GPIO entity', { nodeId: node.id });
        } else if (isFlowNode(node)) {
            validateFlowNode(graph, node, report);
        } else if (node.kind === 'binding') {
            validateBindingNode(graph, node, context, report);
        } else if (getActionTriggers(graph, node.id).length === 0) {
            report('warning', 'action-unconnected', 'Action is not connected to an event or condition', { nodeId: node.id });
        }
    }
    return diagnostics;
}

const isOperator = operator => CONDITION_OPERATORS.includes(operator);

function validateBindingNode(graph, node, context, report) {
    const at = { nodeId: node.id };
    if (!getBindingSource(graph, node.id)?.data?.entity) {
        report('warning', 'binding-no-source', 'Wire the State of a GPIO entity to this binding', at);
    }
    const targets = getBindingTargets(graph, node.id).map(uuid => context.getObject?.(uuid)).filter(Boolean);
    if (targets.length === 0) report('warning', 'binding-no-target', 'Binding drives no object', at);
    if (!node.data?.property || !node.data?.target_property) {
        report('warning', 'binding-incomplete', 'Select the GPIO property and the target property', at);
    }
    const unsupported = targets.map(object => bindingTargetError(node.data, object)).find(Boolean);
    if (unsupported) report('warning', 'binding-unsupported', unsupported, at);
}

function validateFlowNode(graph, node, report) {
    const at = { nodeId: node.id };
    const what = node.kind === 'condition' ? 'Condition' : 'Switch';
    if (getActionTriggers(graph, node.id).length === 0) {
        report('warning', `${node.kind}-no-trigger`, `${what} is not connected to an event`, at);
    }
    if (getActionTriggers(graph, node.id).some(id => reaches(graph, node.id, id))) {
        report('error', 'flow-loop', `${what} runs itself in a loop`, at);
    }
    const subject = getSubjectWire(graph, node.id);
    const subjectNode = subject?.from.node !== undefined ? findNode(graph, subject.from.node) : null;
    const incomplete = node.kind === 'condition'
        ? !(node.data?.conditions?.length > 0) ||
            node.data.conditions.some(item => !item?.property || !isOperator(item.operator))
        : !node.data?.property || (node.data?.cases ?? []).some(item => !isOperator(item?.operator));
    if (incomplete || (subjectNode && !subjectNode.data?.entity)) {
        report('warning', `${node.kind}-incomplete`, `${what} is incomplete`, at);
    }
    if (getFlowOutputPorts(node).every(port => getOutputWires(graph, node.id, port).length === 0)) {
        report('warning', `${node.kind}-no-actions`, `${what} runs no actions`, at);
    }
}

/**
 * The logic belonging to a set of UI objects: event nodes whose source is in
 * `objectUuids`, the action nodes they trigger, and the wires between them.
 */
export function extractObjectsLogic(graph, objectUuids) {
    const uuids = new Set(objectUuids);
    const nodeIds = new Set(graph.nodes
        .filter(node => node.kind === 'event' && uuids.has(getEventSource(graph, node.id)))
        .map(node => node.id));
    // Everything the events run: actions, and conditions and switches with what they run.
    for (let grown = true; grown;) {
        grown = false;
        for (const wire of graph.wires) {
            if (wire.to.port === 'trigger' && nodeIds.has(wire.from.node) && !nodeIds.has(wire.to.node)) {
                nodeIds.add(wire.to.node);
                grown = true;
            }
        }
    }
    // GPIO bindings driving the objects, with the GPIO entity nodes they read.
    for (const node of graph.nodes) {
        if (node.kind !== 'binding' || !getBindingTargets(graph, node.id).some(uuid => uuids.has(uuid))) continue;
        nodeIds.add(node.id);
        const source = getBindingSource(graph, node.id);
        if (source) nodeIds.add(source.id);
    }
    const inside = end => (end.object !== undefined ? uuids.has(end.object) : nodeIds.has(end.node));
    return {
        nodes: structuredClone(graph.nodes.filter(node => nodeIds.has(node.id))),
        wires: structuredClone(graph.wires.filter(wire => inside(wire.from) && inside(wire.to))),
    };
}

/**
 * Copy a subgraph with fresh node/wire ids. Object references are remapped
 * through `objectMap` (old uuid → new uuid); wires to unmapped objects are
 * dropped. Node positions shift by `offset`.
 */
export function cloneLogicSubgraph(subgraph, { objectMap = new Map(), offset = { x: 0, y: 0 } } = {}) {
    return cloneSubgraph(subgraph, { objectMap, offset, idPrefix: LOGIC_ID_PREFIX });
}

/** Append a subgraph (as returned by cloneLogicSubgraph) to a graph. */
export function mergeLogicSubgraph(graph, subgraph) {
    return mergeSubgraph(graph, subgraph);
}
